import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./CustomGrid.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useCompany } from "../../components/context/CompanyContext";
import {
  setFilterText,
  setFilterColumn,
  setSort,
  setPage,
  setAllFilters,
  setVisibleColumns,
  resetGridById,
} from "../../redux/customGridRedux";
import { useSelector, useDispatch } from "react-redux";
import { userRequest, generateFileUrl } from "../../requestMethods";
import { isCellClickable } from "../../util/gridCellClick";
import { formatValue } from "../../util/gridFormatValue";
import { sortGridRows } from "../../util/gridSort";
import { exportGridToPdf } from "../../util/gridPdfExport";
import ExportButton from "./ExportButton";

import CircularProgress from "@mui/material/CircularProgress";

// Operadores disponíveis por tipo de campo (definido fora do componente)
const FILTER_OPERATORS = {
  boolean: [
    { value: "isTrue", label: "é verdadeiro" },
    { value: "isFalse", label: "é falso" },
  ],
  text: [
    { value: "contains", label: "contém" },
    { value: "equals", label: "igual a" },
    { value: "startsWith", label: "começa com" },
    { value: "endsWith", label: "termina com" },
    { value: "isEmpty", label: "está vazio" },
    { value: "isNotEmpty", label: "não está vazio" },
  ],
  number: [
    { value: "equals", label: "=" },
    { value: "notEquals", label: "≠" },
    { value: "greaterThan", label: ">" },
    { value: "greaterThanOrEqual", label: "≥" },
    { value: "lessThan", label: "<" },
    { value: "lessThanOrEqual", label: "≤" },
  ],
  date: [
    { value: "equals", label: "igual a" },
    { value: "greaterThanOrEqual", label: "a partir de" },
    { value: "lessThan", label: "antes de" },
    { value: "lessThanOrEqual", label: "até" },
  ],
};

// Campo sintético da coluna de seleção (checkbox). Permite ordenar e filtrar
// por "selecionado" mesmo não sendo um campo dos dados.
const SELECTION_FIELD = "__selected__";

export default function CustomGrid({
  gridId,
  rows,
  columns,
  getRowId = (r) =>
    r?.id ??
    r?._id ??
    r?.cartId ??
    r?.productId ??
    r?.uuid ??
    JSON.stringify(r),
  pageSize = 10,
  checkboxSelection = false,
  onSelectionChange,
  onRowClick,
  clickableCellsOnly = false,
  className = "",
  tableHeight = "900px",
  selectionField = null,
  selectionModel = [],
  defaultFilterText = "",
  defaultFilterColumn = "",
  serverSide = false,
  rowCount = null,
  onPageChangeServer = null,
  endpopintExport = null,
  objectExport = null,
  callExport = null,
  // Busca todos os registros para exportar usando a MESMA requisição da tela
  // (filtros aplicados + sintéticos + loja + ordenação): async ({ sort }) => rows.
  // Sem ela, o grid usa a requisição genérica de endpopintExport.
  fetchExportRows = null,
  // false quando o backend ignora sort (a tela só ordena a página atual):
  // a exportação ordena tudo no navegador com o critério da tela.
  serverSort = true,
  // Botões da tela (ex.: "Adicionar Cliente") renderizados no início da
  // linha de filtros, em vez de ocupar uma faixa própria acima do grid.
  toolbarActions = null,
}) {
  const dispatch = useDispatch();
  const gridState = useSelector((state) => state.customGrid[gridId] || {});

  const reduxVisibleColumns = useSelector((state) => {
    const stored = state.customGrid?.[gridId]?.visibleColumns;
    return Array.isArray(stored) ? stored : [];
  });

  const reduxKnownColumns = useSelector((state) => {
    const stored = state.customGrid?.[gridId]?.knownColumns;
    return Array.isArray(stored) ? stored : [];
  });

  const pageTitle = useSelector((state) => state.titleRef?.currentTitle);

  const [loading, setLoading] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const reduxFilterText =
    useSelector((state) => state.customGrid[gridId]?.filterText) || "";
  const reduxFilterColumn =
    useSelector((state) => state.customGrid[gridId]?.filterColumn) || "";
  const reduxAllFilters = useSelector(
    (state) => state.customGrid[gridId]?.allFilters || [],
  );
  const [filters, setFilters] = useState(() => {
    return reduxAllFilters.length > 0
      ? reduxAllFilters.map((f) => ({ ...f }))
      : [
          {
            field: defaultFilterColumn || "",
            operator: "contains",
            value: defaultFilterText || "",
          },
        ];
  });

  const filterText = gridState.filterText || "";
  const filterColumn = gridState.filterColumn || "";
  const sortField = gridState.sortField || null;
  const sortDir = gridState.sortDir || null;
  const page = gridState.page || 0;
  const [hoveredCol, setHoveredCol] = useState(null);
  const [colMenu, setColMenu] = useState(null); // { field, dbField, x, y }

  const [internalRows, setInternalRows] = useState(
    Array.isArray(rows) ? rows : [],
  );
  const [selected, setSelected] = useState([]);
  const [colWidths, setColWidths] = useState(() =>
    columns.reduce(
      (acc, col) => ({ ...acc, [col.field]: col.width || 150 }),
      {},
    ),
  );

  const allColumnFields = columns.map((c) => c.field);

  // Retorna colunas que devem ser adicionadas ao estado visível por serem novas
  // (nunca vistas antes). Quando knownColumns existe no Redux, campos ausentes nele
  // são novos; quando não existe (estado antigo persistido), confiamos em
  // visibleColumns como está — sem adicionar nada — para respeitar o que o usuário
  // ocultou anteriormente.
  const calcTrulyNew = (visible, known) => {
    if (!known.length) return [];
    return allColumnFields.filter(
      (f) => !known.includes(f) && !visible.includes(f),
    );
  };

  const [visibleColumns, setLocalVisibleColumns] = useState(() => {
    if (Array.isArray(reduxVisibleColumns) && reduxVisibleColumns.length > 0) {
      const trulyNew = calcTrulyNew(reduxVisibleColumns, reduxKnownColumns);
      return trulyNew.length > 0
        ? [...reduxVisibleColumns, ...trulyNew]
        : reduxVisibleColumns;
    }
    return allColumnFields;
  });

  const { companyId } = useCompany();
  const [temFiltro, setTemFiltro] = useState();

  useEffect(() => {
    if (Array.isArray(reduxVisibleColumns) && reduxVisibleColumns.length > 0) {
      const trulyNew = calcTrulyNew(reduxVisibleColumns, reduxKnownColumns);
      const merged =
        trulyNew.length > 0
          ? [...reduxVisibleColumns, ...trulyNew]
          : reduxVisibleColumns;
      setLocalVisibleColumns(merged);
    }
  }, [reduxVisibleColumns]);

  const toggleColumnVisibility = (field) => {
    const safeVisible = Array.isArray(visibleColumns)
      ? visibleColumns
      : columns.map((c) => c.field);

    const updated = safeVisible.includes(field)
      ? safeVisible.filter((f) => f !== field)
      : [...safeVisible, field];

    setLocalVisibleColumns(updated);

    // knownColumns = todas as colunas atuais; permite distinguir "ocultada" de "nova"
    dispatch(
      setVisibleColumns({ gridId, visibleColumns: updated, knownColumns: allColumnFields }),
    );
  };

  // Filtros ativos no formato esperado pelo backend ({ field: dbField, value, operator })
  const buildActiveServerFilters = () =>
    filters
      .filter((f) => {
        if (["isEmpty", "isNotEmpty", "isTrue", "isFalse"].includes(f.operator)) {
          return !!f.field;
        }
        return f.field && f.value && String(f.value).trim() !== "";
      })
      .map((f) => {
        const colDef = normalizedColumns.find(
          (c) => c.field === f.field || c.dbField === f.field,
        );
        return {
          field: colDef?.dbField ?? f.field,
          value: f.value,
          operator: f.operator || "contains",
        };
      });

  const getRowsExport = async () => {
    setLoading(true);
    try {
      let res, rowsExport;
      // Envia os filtros ativos para o servidor: garante que o export traga TODOS
      // os registros que casam com o filtro (não apenas a página atual carregada).
      const activeFilters = buildActiveServerFilters();
      const sort = currentServerSort();
      if (fetchExportRows) {
        const list = (await fetchExportRows({ filters: activeFilters, sort })) || [];
        rowsExport = objectExport ? list.map((item) => objectExport(item)) : list;
      } else if (callExport === "get") {
        const qs = activeFilters.length
          ? `&allFilters=${encodeURIComponent(JSON.stringify(activeFilters))}`
          : "";
        res = await userRequest.get(
          `${companyId}${endpopintExport}?limit=25000${qs}`,
        );
      } else if (callExport === "post") {
        res = await userRequest.post(`${companyId}${endpopintExport}`, {
          page: 1,
          limit: 25000,
          paginated: true,
          filters: activeFilters,
          // exportMode: backends que suportam (ex: pedidos) omitem colunas pesadas
          // (products) para conseguir exportar milhares de linhas sem estourar.
          exportMode: true,
          ...(sort && { sort }),
        });
      }

      if (!fetchExportRows && res?.data) {
        // Endpoints podem responder como array puro ou como { rows, totalRecords }
        const list = Array.isArray(res.data) ? res.data : res.data.rows || [];
        rowsExport = objectExport ? list.map((item) => objectExport(item)) : list;
      }
      setLoading(false);

      // Mesmo campo/critério do sortedRows da tela (field, não dbField).
      if (rowsExport && !serverSort && sortField && sortDir) {
        rowsExport = sortGridRows(rowsExport, sortField, sortDir);
      }
      return rowsExport;
    } catch (error) {
      setLoading(false);
      return [];
    }
  };
  const handleAddFilter = () => {
    setFilters((prev) => [
      ...prev,
      { field: "", operator: "contains", value: "" },
    ]);
  };

  // 🧩 Remover linha de filtro
  const handleRemoveFilter = (index) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };

  // 🧹 Limpar todos os filtros
  const handleClearFilters = () => {
    setFilters([{ field: "", operator: "contains", value: "" }]);
    dispatch(resetGridById({ gridId }));
  };

  const handleFilterFieldChange = (index, value) => {
    setFilters((prev) => {
      const updated = [...prev];
      const fieldType = getFieldType(value);
      const defaultOperator =
        fieldType === "text"
          ? "contains"
          : fieldType === "boolean"
            ? "isTrue"
            : "equals";
      updated[index] = {
        ...updated[index],
        field: value,
        operator: defaultOperator,
      };
      return updated;
    });

    if (index === 0) {
      dispatch(setFilterColumn({ gridId, filterColumn: value }));
    }
  };

  const handleFilterOperatorChange = (index, operator) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], operator };
    setFilters(newFilters);

    // ✅ usa o newFilters em vez de filters
    dispatch(setAllFilters({ gridId, filters: newFilters }));
  };

  const handleFilterValueChange = (index, value) => {
    const newFilters = [...filters];
    newFilters[index].value = value;
    setFilters(newFilters);

    // ✅ usa o newFilters em vez de filters
    dispatch(setAllFilters({ gridId, filters: newFilters }));
  };

  useEffect(() => {
    if (reduxAllFilters && reduxAllFilters.length > 0) {
      // Só atualiza o estado local se de fato divergir do Redux — evita
      // ping-pong infinito entre este efeito e o que sincroniza filters→Redux.
      setFilters((prev) => {
        const same =
          prev.length === reduxAllFilters.length &&
          reduxAllFilters.every(
            (f, i) =>
              f.field === prev[i].field &&
              f.value === prev[i].value &&
              f.operator === prev[i].operator,
          );
        return same ? prev : reduxAllFilters.map((f) => ({ ...f }));
      });
    } else {
      setFilters((prev) => {
        const target = {
          field: reduxFilterColumn || defaultFilterColumn || "",
          operator: "contains",
          value: reduxFilterText || defaultFilterText || "",
        };
        const same =
          prev.length === 1 &&
          prev[0].field === target.field &&
          prev[0].value === target.value &&
          prev[0].operator === target.operator;
        return same ? prev : [target];
      });
    }
  }, [
    reduxAllFilters,
    reduxFilterColumn,
    reduxFilterText,
    defaultFilterColumn,
    defaultFilterText,
  ]);

  useEffect(() => {
    setInternalRows(Array.isArray(rows) ? rows : []);
  }, [rows]);
  useEffect(() => {
    const same =
      reduxAllFilters.length === filters.length &&
      reduxAllFilters.every(
        (f, i) =>
          f.field === filters[i].field &&
          f.value === filters[i].value &&
          f.operator === filters[i].operator,
      );

    if (!same) {
      dispatch(setAllFilters({ gridId, filters }));
    }
    setTemFiltro(hasFilters(filters));
  }, [filters, gridId, dispatch]);
  // useEffect(() => {
  //   const same =
  //     reduxAllFilters.length === filters.length &&
  //     reduxAllFilters.every(
  //       (f, i) => f.field === filters[i].field && f.value === filters[i].value
  //     );

  //   if (!same) {
  //     setFilters(reduxAllFilters.map((f) => ({ ...f })));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [reduxAllFilters]);
  const arraysEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (String(a[i]) !== String(b[i])) return false;
    }
    return true;
  };
  useEffect(() => {
    // normaliza selectionModel para array
    if (!Array.isArray(selectionModel)) {
      // só atualiza se for diferente do current
      setSelected((prev) => (arraysEqual(prev, []) ? prev : []));
      return;
    }

    let ids = [];
    if (selectionField) {
      const wanted = new Set(selectionModel.map((v) => String(v)));
      ids = (internalRows || [])
        .filter((r) => r && wanted.has(String(r[selectionField])))
        .map((r) => getRowId(r));
    } else {
      ids = selectionModel;
    }

    setSelected((prev) => (arraysEqual(prev, ids) ? prev : ids));
  }, [selectionModel, internalRows, selectionField]);

  useEffect(() => {
    // só aplica defaults quando não há valor salvo no redux para esse gridId
    const saved = gridState || {};
    const savedText = saved.filterText;
    const savedColumn = saved.filterColumn;

    if (!savedText && defaultFilterText) {
      dispatch(setFilterText({ gridId, filterText: defaultFilterText }));
    }
    if (!savedColumn && defaultFilterColumn) {
      dispatch(setFilterColumn({ gridId, filterColumn: defaultFilterColumn }));
    }
  }, [defaultFilterText, defaultFilterColumn, gridId, dispatch, gridState]);

  const normalizedColumns = useMemo(
    () =>
      columns.map((col) => ({
        sortable: true,
        headerName: col.headerName ?? col.header ?? "",
        field: col.field,
        dbField: col.dbField ?? col.field,
        type: col.type,
        format: col.format,
        renderCell: col.renderCell,
        align: col.align,
        headerAlign: col.headerAlign,
        width: colWidths[col.field] || 40,
        description: col.description ?? col.headerName,
        clickable: col.clickable ?? false,
        valueOptions: col.valueOptions || null,
      })),
    [columns, colWidths],
  );

  const filterableColumns = useMemo(() => {
    const cols = normalizedColumns.filter((col) => !!col.type);
    // Coluna de seleção (checkbox) entra como campo booleano filtrável
    if (checkboxSelection) {
      cols.unshift({
        field: SELECTION_FIELD,
        dbField: SELECTION_FIELD,
        headerName: "Selecionado",
        type: "boolean",
      });
    }
    return cols;
  }, [normalizedColumns, checkboxSelection]);

  // Retorna o tipo do campo: prioriza col.type explícito, depois format, depois amostragem
  const getFieldType = useCallback(
    (field) => {
      if (field === SELECTION_FIELD) return "boolean";
      const col = normalizedColumns.find(
        (c) => c.field === field || c.dbField === field,
      );
      if (!col) return "text";

      // Tipo explícito da definição da coluna tem prioridade absoluta
      if (col.type === "boolean") return "boolean";
      if (col.type === "number") return "number";
      if (col.type === "date") return "date";
      if (col.type === "text") return "text";

      // Fallback por format
      if (col.format === "dd/MM/yyyy" || col.format === "dd/MM/yyyy HH:mm:ss") {
        return "date";
      }
      if (col.format === "BRL") {
        return "number";
      }

      return "text";
    },
    [normalizedColumns],
  );

  // Retorna as opções de dropdown do campo (se houver), para filtro tipo select.
  // Normaliza tanto array de strings (["Pendente", ...]) quanto de objetos
  // ([{ value, label }]) para o formato { value, label }.
  const getValueOptionsForField = useCallback(
    (field) => {
      const col = normalizedColumns.find(
        (c) => c.field === field || c.dbField === field,
      );
      if (!col?.valueOptions || col.valueOptions.length === 0) return null;
      return col.valueOptions.map((opt) =>
        typeof opt === "object" && opt !== null
          ? { value: opt.value, label: opt.label ?? String(opt.value) }
          : { value: opt, label: String(opt) },
      );
    },
    [normalizedColumns],
  );

  // Retorna os operadores disponíveis para um campo
  const getOperatorsForField = useCallback(
    (field) => {
      const type = getFieldType(field);
      return FILTER_OPERATORS[type] || FILTER_OPERATORS.text;
    },
    [getFieldType],
  );

  const getDeepValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => {
      if (acc && acc[part] !== undefined && acc[part] !== null) {
        return acc[part];
      }
      return "";
    }, obj);
  };
  // Função para aplicar operador de filtro
  const applyFilterOperator = (
    cellValue,
    filterValue,
    operator,
    fieldType,
    format,
  ) => {
    // Operadores que não precisam de valor
    if (operator === "isEmpty") {
      return cellValue === null || cellValue === undefined || cellValue === "";
    }
    if (operator === "isNotEmpty") {
      return cellValue !== null && cellValue !== undefined && cellValue !== "";
    }

    // Tratamento para boolean
    if (fieldType === "boolean") {
      const boolVal = Boolean(cellValue);
      if (operator === "isTrue") return boolVal === true;
      if (operator === "isFalse") return boolVal === false;
      return true;
    }

    // Para outros operadores, precisa de valor
    if (!filterValue || String(filterValue).trim() === "") return true;

    // Tratamento para datas
    if (
      fieldType === "date" ||
      format === "dd/MM/yyyy" ||
      format === "dd/MM/yyyy HH:mm:ss"
    ) {
      const parseDate = (val) => {
        if (!val) return null;
        const s = String(val);
        // dd/MM/yyyy
        const brMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (brMatch) {
          return new Date(parseInt(brMatch[3]), parseInt(brMatch[2]) - 1, parseInt(brMatch[1]));
        }
        // YYYY-MM-DD (input type=date) — parse como LOCAL para evitar UTC midnight shift
        const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
          return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
        }
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
      };

      const cellDate = parseDate(cellValue);
      const filterDate = parseDate(filterValue);

      if (!cellDate || !filterDate) return false;

      switch (operator) {
        case "equals":
          return cellDate.toDateString() === filterDate.toDateString();
        case "notEquals":
          return cellDate.toDateString() !== filterDate.toDateString();
        case "greaterThan":
          return cellDate > filterDate;
        case "greaterThanOrEqual":
          return cellDate >= filterDate;
        case "lessThan":
          return cellDate < filterDate;
        case "lessThanOrEqual":
          return cellDate <= filterDate;
        default:
          return true;
      }
    }

    // Tratamento para números
    if (fieldType === "number" || format === "BRL") {
      const cellNum = parseFloat(String(cellValue).replace(/[^\d.-]/g, ""));
      const filterNum = parseFloat(String(filterValue).replace(/[^\d.-]/g, ""));

      if (isNaN(cellNum) || isNaN(filterNum)) return false;

      switch (operator) {
        case "equals":
          return cellNum === filterNum;
        case "notEquals":
          return cellNum !== filterNum;
        case "greaterThan":
          return cellNum > filterNum;
        case "greaterThanOrEqual":
          return cellNum >= filterNum;
        case "lessThan":
          return cellNum < filterNum;
        case "lessThanOrEqual":
          return cellNum <= filterNum;
        default:
          return true;
      }
    }

    // Tratamento para texto (padrão)
    const cellStr = String(cellValue ?? "").toLowerCase();
    const filterStr = filterValue.toLowerCase();

    switch (operator) {
      case "contains":
        return cellStr.includes(filterStr);
      case "equals":
        return cellStr === filterStr;
      case "startsWith":
        return cellStr.startsWith(filterStr);
      case "endsWith":
        return cellStr.endsWith(filterStr);
      case "notEquals":
        return cellStr !== filterStr;
      default:
        return cellStr.includes(filterStr);
    }
  };

  const filteredRows = useMemo(() => {
    if (!rows) return [];

    // Em serverSide o backend já filtrou — não re-filtra client-side (evita perder
    // linhas com operadores não suportados aqui, ex: "in").
    if (serverSide) return rows;

    const activeFilters = filters.filter((f) => {
      // Filtros que não precisam de valor
      if (f.operator === "isEmpty" || f.operator === "isNotEmpty") {
        return f.field;
      }
      if (f.operator === "isTrue" || f.operator === "isFalse") {
        return f.field;
      }
      return f.field && f.value && String(f.value).trim() !== "";
    });

    if (activeFilters.length === 0) return rows;

    return rows.filter((row) =>
      activeFilters.every((f) => {
        const colDef = normalizedColumns.find(
          (c) => c.field === f.field || c.dbField === f.field,
        );
        const realField = colDef?.dbField ?? f.field;
        const format = colDef?.format;
        const fieldType = getFieldType(f.field);
        // Campo de seleção: valor é se a linha está selecionada
        const cellValue =
          f.field === SELECTION_FIELD
            ? selected.includes(getRowId(row))
            : row[realField];

        return applyFilterOperator(
          cellValue,
          f.value,
          f.operator || "contains",
          fieldType,
          format,
        );
      }),
    );
  }, [rows, filters, normalizedColumns, getFieldType, selected, getRowId, serverSide]);

  const sortedRows = useMemo(() => {
    if (!sortField || !sortDir) return [...filteredRows];

    // Ordenação pela coluna de seleção (selecionados primeiro/por último)
    if (sortField === SELECTION_FIELD) {
      return [...filteredRows].sort((a, b) => {
        const sa = selected.includes(getRowId(a)) ? 1 : 0;
        const sb = selected.includes(getRowId(b)) ? 1 : 0;
        return sortDir === "asc" ? sa - sb : sb - sa;
      });
    }

    return sortGridRows(filteredRows, sortField, sortDir);
  }, [filteredRows, sortField, sortDir, selected, getRowId]);

  const totalRows = serverSide ? (rowCount ?? 0) : sortedRows.length;

  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageRows = serverSide
    ? sortedRows // aplicar sort também
    : sortedRows.slice(page * pageSize, page * pageSize + pageSize);

  const handleFilterTextChange = (text) => {
    dispatch(setFilterText({ gridId, filterText: text }));
    dispatch(setPage({ gridId, page: 0 }));
  };
  const handleFilterColumnChange = (col) => {
    dispatch(setFilterColumn({ gridId, filterColumn: col }));
    dispatch(setPage({ gridId, page: 0 }));
  };
  // Ordenação atual no formato do servidor (campo mapeado para dbField)
  const currentServerSort = () => {
    if (!sortField || !sortDir) return null;
    const colDef = normalizedColumns.find((c) => c.field === sortField);
    return { field: colDef?.dbField ?? sortField, dir: sortDir };
  };

  const handleSortChange = (field) => {
    let dir = "asc";
    if (sortField === field)
      dir = sortDir === "asc" ? "desc" : sortDir === "desc" ? null : "asc";
    dispatch(setSort({ gridId, sortField: dir ? field : null, sortDir: dir }));
    dispatch(setPage({ gridId, page: 0 }));

    // Em grids server-side a ordenação precisa recarregar do servidor (senão só
    // ordena a página atual em memória — quebra ex.: ordenar por % Desconto).
    if (serverSide && onPageChangeServer) {
      const colDef = normalizedColumns.find((c) => c.field === field);
      const sort = dir ? { field: colDef?.dbField ?? field, dir } : null;
      onPageChangeServer(0, pageSize, {
        filters: buildActiveServerFilters(),
        sort,
      });
    }
  };
  const handlePageChange = (newPage) => {
    dispatch(setPage({ gridId, page: newPage }));

    if (serverSide && onPageChangeServer) {
      onPageChangeServer(newPage, pageSize, {
        filters: buildActiveServerFilters(),
        sort: currentServerSort(),
      });
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const exists = prev.includes(id);
      const next = exists ? prev.filter((x) => x !== id) : [...prev, id];

      if (onSelectionChange) {
        if (selectionField) {
          const selectedValues = internalRows
            .filter((r) => next.includes(getRowId(r)))
            .map((r) => r[selectionField]);

          const changedValues = internalRows
            .filter((r) => r && getRowId(r) === id)
            .map((r) => r[selectionField]);

          // ⚙️ segue o padrão do MUI: primeiro = todos, segundo = alterados
          onSelectionChange(selectedValues, changedValues);
        } else {
          onSelectionChange(next, [id]);
        }
      }

      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const idsOnPage = pageRows.map(getRowId);
    const allSelected = idsOnPage.every((id) => selected.includes(id));
    const next = allSelected
      ? selected.filter((id) => !idsOnPage.includes(id))
      : Array.from(new Set([...selected, ...idsOnPage]));

    const changed = allSelected ? idsOnPage : idsOnPage;

    if (onSelectionChange) {
      if (selectionField) {
        const selectedValues = internalRows
          .filter((r) => next.includes(getRowId(r)))
          .map((r) => r[selectionField]);

        const changedValues = internalRows
          .filter((r) => changed.includes(getRowId(r)))
          .map((r) => r[selectionField]);

        onSelectionChange(selectedValues, changedValues);
      } else {
        onSelectionChange(next, changed);
      }
    }

    setSelected(next);
  };

  const startResize = (e, field) => {
    e.preventDefault();
    e.stopPropagation(); // Evita que o evento chegue ao th
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = colWidths[field];

    const onMouseMove = (ev) => {
      const newWidth = Math.max(40, startWidth + ev.clientX - startX);
      setColWidths((prev) => ({ ...prev, [field]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      // Pequeno delay para evitar que o click seja disparado
      setTimeout(() => setIsResizing(false), 100);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const hasFilters = (arr) => {
    if (!Array.isArray(arr)) return false;
    return arr.some((item) => {
      if (!item.field) return false;
      if (["isEmpty", "isNotEmpty", "isTrue", "isFalse"].includes(item.operator)) return true;
      return item.value && String(item.value).trim() !== "";
    });
  };

  // Linhas para exportar (Excel e PDF): todos os registros, não só a página.
  // fallbackRows é o que usar quando não há busca no servidor.
  const loadRowsForExport = async (fallbackRows) => {
    let rowsExport;
    if (serverSide && (endpopintExport || fetchExportRows)) {
      // Em grids server-side os dados em memória são só a página atual; buscamos
      // do servidor (com ou sem filtro) para exportar o conjunto completo.
      rowsExport = await getRowsExport();
    } else if (!hasFilters(filters)) {
      rowsExport = await getRowsExport();
    }
    return Array.isArray(rowsExport) ? rowsExport : fallbackRows;
  };

  const exportToPdf = async (orientation) => {
    // Só as colunas que estão na tela, na ordem da tela; sortedRows mantém a
    // ordenação atual quando os dados já estão em memória.
    const onScreen = normalizedColumns.filter((c) =>
      Array.isArray(visibleColumns) ? visibleColumns.includes(c.field) : true,
    );
    const dataRows = await loadRowsForExport(sortedRows);
    // Baixar as fotos pode demorar: mantém o indicador de carregamento até o save.
    setLoading(true);
    try {
      await exportGridToPdf({
        columns: onScreen,
        rows: dataRows,
        orientation,
        title: pageTitle?.title,
        resolveImageUrl: generateFileUrl,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    const headers = normalizedColumns.map((c) => c.headerName || c.field);

    let dataRows = await loadRowsForExport(sortedRows);

    // Detecta colunas numéricas (versão original sua)
    const numericColumns = normalizedColumns.reduce((acc, col) => {
      const isNumeric = dataRows.every((row) => {
        const val = row[col.dbField];

        if (val === null || val === "" || val === undefined) return true;
        const n = Number(val);
        return !isNaN(n) && isFinite(n);
      });

      if (isNumeric) acc[col.dbField] = true;
      return acc;
    }, {});

    // Função para converter string -> Date considerando formatos
    const parseDate = (value, format) => {
      if (!value) return null;

      // dd/MM/yyyy HH:mm:ss
      if (format === "dd/MM/yyyy HH:mm:ss") {
        const regex = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;
        const match = value.match(regex);
        if (match) {
          const [, dd, mm, yyyy, HH, MM, SS] = match;
          return new Date(`${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`);
        }
      }

      // dd/MM/yyyy
      if (format === "dd/MM/yyyy") {
        const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const match = value.match(regex);
        if (match) {
          const [, dd, mm, yyyy] = match;
          return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
        }
      }

      // fallback (ISO, timestamps)
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    // Monta AOA
    const data = dataRows.map((r) =>
      normalizedColumns.map((c) => {
        const value = r[c.dbField];

        // 👉 TRATAR DATAS
        if (c.format === "dd/MM/yyyy HH:mm:ss" || c.format === "dd/MM/yyyy") {
          const d = parseDate(value, c.format);
          return d instanceof Date && !isNaN(d.getTime()) ? d : value;
        }

        // NÚMEROS
        if (numericColumns[c.dbField]) {
          const num = Number(value);
          return isNaN(num) ? null : num;
        }

        // BRL
        if (c.format === "BRL") {
          const num = Number(value);
          return isNaN(num) ? value : num;
        }

        // demais formatos
        return formatValue(value, c.format);
      }),
    );

    // Criar Sheet
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    // Formatar colunas de data no Excel
    normalizedColumns.forEach((c, colIdx) => {
      if (c.format !== "dd/MM/yyyy" && c.format !== "dd/MM/yyyy HH:mm:ss") {
        return;
      }

      for (let r = 2; r <= data.length + 1; r++) {
        const cellAddress = XLSX.utils.encode_cell({ r: r - 1, c: colIdx });
        const cell = ws[cellAddress];
        if (!cell) continue;

        if (cell.v instanceof Date) {
          cell.t = "d";
          cell.z = c.format; // Excel interpreta perfeitamente
        }
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dados");

    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], {
        type: "application/octet-stream",
      }),
      "dados_exportados.xlsx",
    );
  };

  useEffect(() => {
    if (!colMenu) return;
    const close = () => setColMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [colMenu]);

  const handleAddToFilter = (field, dbField) => {
    setColMenu(null);
    setFilters((prev) => {
      const fieldType = getFieldType(field);
      const defaultOperator =
        fieldType === "boolean" ? "isTrue" : fieldType === "number" ? "equals" : "contains";
      // reutiliza primeiro filtro se estiver vazio
      if (prev.length === 1 && !prev[0].field) {
        dispatch(setFilterColumn({ gridId, filterColumn: dbField }));
        return [{ field: dbField, operator: defaultOperator, value: "" }];
      }
      // não duplica
      if (prev.some((f) => f.field === dbField)) return prev;
      return [...prev, { field: dbField, operator: defaultOperator, value: "" }];
    });
  };

  const handleSearch = () => {
    if (!serverSide) return;

    const activeFilters = filters.filter(
      (f) => f.field && f.value && String(f.value).trim() !== "",
    );

    dispatch(setPage({ gridId, page: 0 }));
    onPageChangeServer(0, pageSize, {
      filters: activeFilters.map((f) => {
        const colDef = normalizedColumns.find((c) => c.field === f.field);
        return {
          field: colDef?.dbField ?? f.field,
          value: f.value,
          operator: f.operator || "contains",
        };
      }),
      sort: currentServerSort(),
    });
  };

  return (
    <div
      className={`w-full overflow-auto bg-white rounded shadow-sm p-2 apple-grid ${className}`}
      style={{
        maxHeight: tableHeight,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="filter-container">
        <div className="filter-label">Filtros</div>
        <div className="filter-row">
          {toolbarActions && (
            <div className="filter-toolbar-actions">{toolbarActions}</div>
          )}
          <select
            className="filter-field"
            value={filters[0]?.field || ""}
            onChange={(e) => handleFilterFieldChange(0, e.target.value)}
          >
            <option value="">Campo...</option>
            {filterableColumns.map((col) => (
              <option key={col.field} value={col.dbField}>
                {col.headerName || col.field}
              </option>
            ))}
          </select>

          <select
            className="filter-operator"
            value={filters[0]?.operator || "contains"}
            onChange={(e) => handleFilterOperatorChange(0, e.target.value)}
            style={{ minWidth: 90 }}
          >
            {getOperatorsForField(filters[0]?.field).map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>

          <div className="filter-value-group">
            {filters[0]?.operator !== "isEmpty" &&
              filters[0]?.operator !== "isNotEmpty" &&
              filters[0]?.operator !== "isTrue" &&
              filters[0]?.operator !== "isFalse" &&
              (getValueOptionsForField(filters[0]?.field) ? (
                <select
                  className="filter-value"
                  value={filters[0]?.value || ""}
                  onChange={(e) => handleFilterValueChange(0, e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {getValueOptionsForField(filters[0]?.field).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="filter-value"
                  type={
                    getFieldType(filters[0]?.field) === "date"
                      ? "date"
                      : getFieldType(filters[0]?.field) === "number"
                        ? "number"
                        : "text"
                  }
                  placeholder="Valor..."
                  value={filters[0]?.value || ""}
                  onChange={(e) => handleFilterValueChange(0, e.target.value)}
                />
              ))}

            <div className="filter-buttons">
              <button
                type="button"
                className="filter-add"
                onClick={handleAddFilter}
                title="Adicionar filtro"
              >
                +
              </button>
              {filters.length > 1 && (
                <button
                  type="button"
                  className="filter-remove"
                  onClick={() => handleRemoveFilter(filters.length - 1)}
                  title="Remover último filtro"
                >
                  −
                </button>
              )}
            </div>

            <button
              type="button"
              className="filter-search"
              onClick={handleSearch}
              title="Buscar"
            >
              Buscar
            </button>
            {temFiltro && (
              <button
                type="button"
                className="filter-clear"
                onClick={handleClearFilters}
                title="Limpar todos os filtros"
              >
                Limpar
              </button>
            )}

            <div className="filter-export" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <ExportButton
                onExcel={exportToExcel}
                onPdf={exportToPdf}
                disabled={loading}
                label={
                  <span style={{ fontSize: 10, color: "#444" }}>
                    {/* serverSide: totalRows já é o total do filtro (vindo do backend).
                        Client-side: usa a contagem filtrada local. */}
                    {serverSide ? totalRows : !temFiltro ? totalRows : filteredRows.length} reg.
                  </span>
                }
              />
              {loading ? <CircularProgress size={16} /> : false}
            </div>
          </div>
        </div>

        {/* Linha 2 (e demais, caso haja mais filtros) */}
        {filters.slice(1).map((f, i) => (
          <div key={i + 1} className="filter-row">
            <select
              className="filter-field"
              value={f.field}
              onChange={(e) => handleFilterFieldChange(i + 1, e.target.value)}
            >
              <option value="">Campo...</option>
              {filterableColumns.map((col) => (
                <option key={col.field} value={col.dbField}>
                  {col.headerName || col.field}
                </option>
              ))}
            </select>

            <select
              className="filter-operator"
              value={f.operator || "contains"}
              onChange={(e) =>
                handleFilterOperatorChange(i + 1, e.target.value)
              }
              style={{ minWidth: 90 }}
            >
              {getOperatorsForField(f.field).map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>

            {f.operator !== "isEmpty" &&
              f.operator !== "isNotEmpty" &&
              f.operator !== "isTrue" &&
              f.operator !== "isFalse" &&
              (getValueOptionsForField(f.field) ? (
                <select
                  className="filter-value"
                  value={f.value || ""}
                  onChange={(e) =>
                    handleFilterValueChange(i + 1, e.target.value)
                  }
                >
                  <option value="">Selecione...</option>
                  {getValueOptionsForField(f.field).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="filter-value"
                  type={
                    getFieldType(f.field) === "date"
                      ? "date"
                      : getFieldType(f.field) === "number"
                        ? "number"
                        : "text"
                  }
                  placeholder="Valor..."
                  value={f.value}
                  onChange={(e) =>
                    handleFilterValueChange(i + 1, e.target.value)
                  }
                />
              ))}
          </div>
        ))}

      </div>

      <div className="table-container">
        <div style={{ marginBottom: 10 }}>
          <details>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Colunas
            </summary>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                paddingLeft: 15,
                marginTop: 5,
              }}
            >
              {normalizedColumns.map((col) => (
                <label key={col.field} style={{ marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={
                      Array.isArray(visibleColumns) &&
                      visibleColumns.includes(col.field)
                    }
                    onChange={() => toggleColumnVisibility(col.field)}
                  />{" "}
                  {col.headerName || col.field}
                </label>
              ))}
            </div>
          </details>
        </div>

        <table>
          <thead>
            <tr>
              {checkboxSelection && (
                <th style={{ width: "40px", textAlign: "center" }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        pageRows.length > 0 &&
                        pageRows.every((r) => selected.includes(getRowId(r)))
                      }
                      onChange={toggleSelectAllOnPage}
                    />
                    <span
                      className="sort-indicator"
                      style={{ cursor: "pointer" }}
                      title="Ordenar por selecionados"
                      onClick={() => handleSortChange(SELECTION_FIELD)}
                    >
                      {sortField === SELECTION_FIELD
                        ? sortDir === "asc"
                          ? "▲"
                          : "▼"
                        : "⇅"}
                    </span>
                  </div>
                </th>
              )}
              {normalizedColumns
                .filter((c) =>
                  Array.isArray(visibleColumns)
                    ? visibleColumns.includes(c.field)
                    : true,
                )
                .map((col) => {
                  const headerTextAlign =
                    col.headerAlign ?? col.align ?? "left";
                  return (
                    <th
                      key={col.field}
                      onClick={(e) => {
                        if (isResizing) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        e.stopPropagation();
                        setColMenu({ field: col.field, dbField: col.dbField, x: e.clientX, y: e.clientY });
                      }}
                      onMouseEnter={() => setHoveredCol(col.field)}
                      onMouseLeave={() => setHoveredCol(null)}
                      className={hoveredCol === col.field ? "hovered" : ""}
                      style={{
                        textAlign: headerTextAlign,
                        cursor: col.sortable ? "pointer" : "default",
                        width: `${colWidths[col.field]}px`,
                        minWidth: `40px`,
                        position: "relative",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <div
                        className={`header-with-tooltip ${
                          col.headerAlign === "center"
                            ? "center"
                            : col.headerAlign === "right"
                              ? "right"
                              : ""
                        }`}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>{col.headerName ?? col.field}</span>
                          <span className="sort-indicator">
                            {sortField === col.field
                              ? sortDir === "asc"
                                ? "▲"
                                : "▼"
                              : "⇅"}
                          </span>
                        </span>

                        {col.description && (
                          <span className="cg-tooltip">{col.description}</span>
                        )}

                        <div
                          className="resize-handle"
                          onMouseDown={(e) => startResize(e, col.field)}
                        />
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>

          <tbody>
            {pageRows.length > 0 ? (
              pageRows.map((row) => {
                const id = getRowId(row);
                return (
                  <tr
                    key={id}
                    onClick={() => {
                      if (onRowClick && !clickableCellsOnly) onRowClick(row);
                    }}
                    className={selected.includes(id) ? "selected" : ""}
                  >
                    {checkboxSelection && (
                      <td style={{ width: "40px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selected.includes(id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelect(id);
                          }}
                        />
                      </td>
                    )}
                    {normalizedColumns
                      .filter((c) =>
                        Array.isArray(visibleColumns)
                          ? visibleColumns.includes(c.field)
                          : true,
                      )
                      .map((col) => {
                        const cellValue = row[col.field];
                        const textAlign = col.align ?? col.align ?? "left";
                        const display = col.renderCell
                          ? col.renderCell({
                              row,
                              value: cellValue,
                              field: col.field,
                            })
                          : formatValue(cellValue, col.format);
                        const cellClickable = isCellClickable({
                          hasRowClick: !!onRowClick,
                          clickableCellsOnly,
                          column: col,
                        });
                        return (
                          <td
                            key={col.field}
                            style={{
                              textAlign: textAlign,
                              width: `${colWidths[col.field]}px`,
                              minWidth: `40px`,
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              cursor: cellClickable ? "pointer" : "default",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cellClickable) {
                                onRowClick(row);
                              }
                            }}
                          >
                            {display}
                          </td>
                        );
                      })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    (checkboxSelection ? 1 : 0) + normalizedColumns.length
                  }
                  className="text-center muted"
                >
                  Nenhum registro encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pagination">
          <div>
            Mostrando {page * pageSize + 1} -
            {Math.min((page + 1) * pageSize, totalRows)} de {totalRows}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => handlePageChange(0)} disabled={page === 0}>
              «
            </button>
            <button
              onClick={() => handlePageChange(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              ‹
            </button>
            <span>
              Página {page + 1} / {pageCount}
            </span>
            <button
              onClick={() =>
                handlePageChange(Math.min(pageCount - 1, page + 1))
              }
              disabled={page >= pageCount - 1}
            >
              ›
            </button>
            <button
              onClick={() => handlePageChange(pageCount - 1)}
              disabled={page >= pageCount - 1}
            >
              »
            </button>
          </div>
        </div>
      </div>

      {colMenu && (
        <div
          className="col-menu"
          style={{ top: colMenu.y, left: colMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="col-menu-item"
            onClick={() => { handleSortChange(colMenu.field); setColMenu(null); }}
          >
            <span className="col-menu-icon">⇅</span> Ordenar
          </button>
          <button
            className="col-menu-item"
            onClick={() => handleAddToFilter(colMenu.field, colMenu.dbField)}
          >
            <span className="col-menu-icon">+</span> Adicionar ao filtro
          </button>
        </div>
      )}
    </div>
  );
}
